// A deploy replaces every content-hashed page chunk and deletes the previous
// build's files, so a tab left open across one asks for a url that no longer
// exists and webpack raises a ChunkLoadError. Reloading picks up the new build.
//
// Reloading *unconditionally* is what left the site refreshing itself: if the
// fresh build still can't load the chunk — a half-finished rollout, a chunk
// missing from the pod that answered — the reload hits the same error and goes
// round again, with nothing on screen to say why. So: reload at most once per
// session, then let the error surface.

export const CHUNK_RELOAD_KEY = 'chunk-reload-attempted';

/**
 * Record that we are about to reload because of a missing chunk.
 *
 * @param {Storage} [storage] defaults to sessionStorage; injectable for tests.
 * @returns {boolean} true if this is the first attempt and reloading is worth
 *                    a try, false if we have already tried and should stop.
 */
export function rememberChunkReload(storage = defaultStorage()) {
    try {
        if (!storage || storage.getItem(CHUNK_RELOAD_KEY)) {
            return false;
        }

        storage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));

        return true;
    } catch (_) {
        // Private mode and blocked storage both throw on access. Without
        // somewhere to record the attempt we can't tell a first failure from a
        // loop, and a visible error beats an endless refresh.
        return false;
    }
}

/** Called once a chunk loads, so a later deploy gets its own single retry. */
export function forgetChunkReload(storage = defaultStorage()) {
    try {
        if (storage) {
            storage.removeItem(CHUNK_RELOAD_KEY);
        }
    } catch (_) {
        // Nothing to clear if we could never record it.
    }
}

function defaultStorage() {
    try {
        return window.sessionStorage;
    } catch (_) {
        return null;
    }
}
